import { JSX, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory, useRouteMatch } from "react-router-dom";
import { ColumnConfig, SortDirections, TextAlignment, wafflesKeyColumnName } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { NetworkIntegrityAggregate, NetworkIntegrityExcessWarningBreakdownResult } from "@app/projects/network-assessment/network-integrity/models";
import { NetworkIntegrityAggregateGroup } from "@app/projects/enumerators";
import NetworkIntegrityService from "@app/projects/network-assessment/network-integrity/networkIntegrityService";
import { NetworkIntegrityProviderTypes, NetworkIntegrityDisplayMode } from "@app/projects/network-assessment/network-integrity/filters/networkIntegrityFilterConstants";
import { NetworkIntegrityFilterAction} from "@app/projects/network-assessment/network-integrity/state-management/networkIntegrityFilterStateManagement";
import { NetworkIntegrityContext } from "@app/projects/network-assessment/network-integrity/NetworkIntegrityContext";
import NetworkIntegrityFilterContainer from "@app/projects/network-assessment/network-integrity/filters/NetworkIntegrityFilterContainer";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import Page from "@app/shared/Page";
import PageHeader from "@app/shared/PageHeader";
import PageHeaderRight from "@app/shared/PageHeaderRight";
import PageInner from "@app/shared/PageInner";
import PageSection from "@app/shared/PageSection";
import { NetworkIntegrityChangeArrow } from "@app/projects/network-assessment/network-integrity/widgets/NetworkIntegrityChangeArrow";
import { isNpiTypeEnabled } from "@app/projects/network-assessment/network-integrity/helpers/networkIntegrityNpiTypeHelper";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

interface ExcessWarningsTableData {
  [wafflesKeyColumnName]: string;
  breakdownBucket: number;
  providersCount: JSX.Element;
  totalAfterTermed?: JSX.Element;
  change?: JSX.Element;
  percentChange?: JSX.Element;
  rawAfterTermedCount?: number;
  rawProviderCount?: number;
  rawChange?: number;
  rawPercentChange?: number;
}

interface RouteMatchParams {
  aggregateGroup: NetworkIntegrityAggregateGroup;
  projectId: string;
  projectRunConfigurationId: string;
  providerType: NetworkIntegrityProviderTypes;
}

const ExcessWarningsBreakdownPage = (): JSX.Element => {
  const routeMatch = useRouteMatch<RouteMatchParams>();
  const history = useHistory();
  const networkIntegrityService = useMemo(() => new NetworkIntegrityService(), []);
  const networkIntegrityContext = useContext(NetworkIntegrityContext);
  const ffContext = useContext(FeatureFlagContext);
  const showTotalAfterTermed = networkIntegrityContext.displayMode === NetworkIntegrityDisplayMode.TotalAfterTermed
  const project = networkIntegrityContext.project;

  const { aggregateGroup, projectId, projectRunConfigurationId, providerType: routeProviderType } = routeMatch.params;
  const hasSyncedUrlRef = useRef(false);
  const networkIntegrityContextRef = useRef(networkIntegrityContext);
  const npiEntityTypeFilter = isNpiTypeEnabled(ffContext.enabledFeatureFlags, aggregateGroup)
    ? networkIntegrityContext.npiType ?? null
    : null;

  const [loading, setLoading] = useState<boolean>(true);
  const [warningsResults, setWarningsResults] = useState<NetworkIntegrityExcessWarningBreakdownResult[]>(null);
  const [selectedWarnings, setSelectedWarnings] = useState<string[]>([]);
  const [showConfidenceLevel, setShowConfidenceLevel] = useState(false);
  const warningType = aggregateGroup === NetworkIntegrityAggregateGroup.ExcessAddresses ? "Addresses" : "Specialties";

  useEffect(() => {
    if (!project) return;
    const shouldShowConfidenceLevels = (project.showNetworkIntegrityCriticalAlerts ?? false) && aggregateGroup == NetworkIntegrityAggregateGroup.ExcessAddresses;
    setShowConfidenceLevel(shouldShowConfidenceLevels);
  }, [project, aggregateGroup]);

  useEffect(() => {
    if (networkIntegrityContext.isLoading) return;

    let cancelled = false;

    async function fetchExcessWarnings() {
      setLoading(true);
      const results = await networkIntegrityService.getExcessWarningBreakdownResults(Number(projectId), networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId, networkIntegrityContext.providerType, aggregateGroup, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, npiEntityTypeFilter);
      if (!cancelled) {
        // initially sort in descending order
        results.sort((a, b) => parseInt(b.aggregate.aggregateKey, 10) - parseInt(a.aggregate.aggregateKey, 10));
        setWarningsResults(results);
        setLoading(false);
      }
    };

    fetchExcessWarnings();

    return () => { cancelled = true; };
  }, [networkIntegrityContext.isLoading, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType, networkIntegrityContext.regionSetIds, networkIntegrityContext.specialtyCodes, npiEntityTypeFilter]);

  // Only provider type and run configuration live in the URL, so only they push a history entry.
  useEffect(() => {
    if (networkIntegrityContext.isLoading) return;

    const isMountSync = !hasSyncedUrlRef.current;
    hasSyncedUrlRef.current = true;

    const currentRunConfigId = networkIntegrityContext.projectRunConfigurationId ?? projectRunConfigurationId;
    if (routeProviderType === networkIntegrityContext.providerType && projectRunConfigurationId === String(currentRunConfigId)) {
      return;
    }

    const path = `/projects/${projectId}/${currentRunConfigId}/network-integrity/excess-warnings-breakdown/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}`;
    // A mismatch found on load is reconciliation, not a user navigation, so it must not add a Back entry.
    if (isMountSync) {
      history.replace(path);
    } else {
      history.push(path);
    }
  }, [networkIntegrityContext.isLoading, networkIntegrityContext.projectRunConfigurationId, networkIntegrityContext.providerType]);

  useEffect(() => {
    networkIntegrityContextRef.current = networkIntegrityContext;
  });

  useEffect(() => {
  // because url is pushed on provider type change, listen for back/forward navigation to update the state accordingly
  const unsubscribe = history.listen((location, action) => {
    if (action === "POP" && location.pathname.includes("/network-integrity/excess-warnings-breakdown/")) {
      const pathParts = location.pathname.split('/');
        const newProviderRunConfigurationId = pathParts[3];
        const newProviderType = pathParts[6];
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProviderTypeFilterChanged, value: { providerType: newProviderType } });
        networkIntegrityContext.filtersChanged({ type: NetworkIntegrityFilterAction.ProjectRunConfigurationIdChanged, value: { projectRunConfigurationId: newProviderRunConfigurationId } });
    } else if (action == "POP" && location.pathname.includes(`${projectId}/network-integrity`)) {
      // if navigating back goes to the main network integrity page, update the provider type there as well
      // Replace, not push: the user is already on this entry, and a push would duplicate it and drop Forward.
      // Read through the ref: this listener only re-subscribes on pathname change, so the closure's context is stale.
      const currentContext = networkIntegrityContextRef.current;
      history.replace({
        pathname: `/projects/${projectId}/network-integrity`,
        state: {
          initialProviderType: currentContext.providerType,
          initialRegionSetIds: currentContext.regionSetIds,
          initialSpecialtyCodes: currentContext.specialtyCodes,
          runConfiguration: currentContext.projectRunConfiguration
        }
      });
    }
  });

  return () => {
    unsubscribe();
  };
}, [history.location.pathname]);

  const createLinkToProvidersPage = (breakdownBucket: string, providersCount: number): JSX.Element => {
    return <Link
      to={{
        pathname: `/projects/${projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/providers/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}/${encodeURIComponent(breakdownBucket)}`,
        state: {
          initialExcessWarning: `${breakdownBucket} ${warningType}`,
          initialNpiType: npiEntityTypeFilter ?? undefined,
          initialRegionSetIds: networkIntegrityContext.regionSetIds,
          initialSpecialtyCodes: networkIntegrityContext.specialtyCodes,
          runConfiguration: networkIntegrityContext.projectRunConfiguration
        }
      }}>
      {providersCount.toLocaleString()}
    </Link>
  };

  const renderChangeCell = (change: number): JSX.Element => {
    if (change < 0) {
      return (
        <span className="ni-table__decrease-indicator">
          {change.toLocaleString()}
          <NetworkIntegrityChangeArrow direction="decrease" />
        </span>
      );
    }
    return (
      <span className="ni-table__no-change-indicator">
        0
        <NetworkIntegrityChangeArrow direction="no-change" />
      </span>
    );
  };

  const renderPercentChangeCell = (change: number, percentChange: number): JSX.Element => {
    if (change < 0) {
      return (
        <span className="ni-table__decrease-indicator">
          {percentChange.toFixed(1)}%
          <NetworkIntegrityChangeArrow direction="decrease" />
        </span>
      );
    }
    return (
      <span className="ni-table__no-change-indicator">
        0%
        <NetworkIntegrityChangeArrow direction="no-change" />
      </span>
    );
  };

  /* oxlint-disable sort-keys */
  const mapWarningToTableData = (warning: NetworkIntegrityAggregate): ExcessWarningsTableData => {
    const current = warning.count;

    if (!showTotalAfterTermed) {
      return {
        [wafflesKeyColumnName]: warning.aggregateKey,
        breakdownBucket: parseInt(warning.aggregateKey, 10),
        providersCount: createLinkToProvidersPage(warning.aggregateKey, current),
        rawAfterTermedCount: warning.afterTermedCount ?? undefined,
        rawProviderCount: current,
      };
    }

    const afterTermed = warning.afterTermedCount ?? current;
    const change = afterTermed - current;
    const percentChange = current !== 0 ? (change / current) * 100 : 0;

    return {
      [wafflesKeyColumnName]: warning.aggregateKey,
      breakdownBucket: parseInt(warning.aggregateKey, 10),
      providersCount: createLinkToProvidersPage(warning.aggregateKey, current),
      totalAfterTermed: createLinkToProvidersPage(warning.aggregateKey, afterTermed),
      change: renderChangeCell(change),
      percentChange: renderPercentChangeCell(change, percentChange),
      rawAfterTermedCount: warning.afterTermedCount ?? undefined,
      rawProviderCount: current,
      rawChange: change,
      rawPercentChange: percentChange,
    };
  };
  /* oxlint-enable sort-keys */

  const customSort = (segment: ExcessWarningsTableData[], column: keyof ExcessWarningsTableData, direction: SortDirections) => {
    if (direction === SortDirections.None)
      return segment;

    const data = warningsResults.map(warning => mapWarningToTableData(warning.aggregate));
    const multiplier = direction === SortDirections.Asc ? 1 : -1;

    const byNumber = (selector: (row: ExcessWarningsTableData) => number | undefined) =>
      data.sort((a, b) => ((selector(a) ?? 0) - (selector(b) ?? 0)) * multiplier);

    switch (column) {
      case "providersCount":
        return showTotalAfterTermed
          ? byNumber(r => r.rawProviderCount)
          : byNumber(r => r.rawAfterTermedCount ?? r.rawProviderCount);
      case "totalAfterTermed":
        return byNumber(r => r.rawAfterTermedCount ?? r.rawProviderCount);
      case "change":
        return byNumber(r => r.rawChange);
      case "percentChange":
        return byNumber(r => r.rawPercentChange);
      case "breakdownBucket":
        return byNumber(r => r.breakdownBucket);
      default:
        return data.sort((a, b) => (a[column] > b[column] ? 1 : -1) * multiplier);
    }
  };

  const onCheckboxChange = useCallback((newSelectedKeys: string[], isBulk?: boolean) => {
    if (isBulk) {
      if (newSelectedKeys.length === 0) {
        setSelectedWarnings([]);
      } else {
        setSelectedWarnings(warningsResults.map(w => w.aggregate.aggregateKey.toString()));
      }
    } else {
      setSelectedWarnings(newSelectedKeys);
    }
  }, [selectedWarnings, warningsResults]);

  const calculateReportsRowCount = (): number => {
    if (!warningsResults) {
      return 0;
    }

    // If no warning is selected, report includes all items
    if (selectedWarnings.length === 0) {
      return warningsResults.reduce((sum, current) => sum + current.reportRowCount, 0);
    }

    return warningsResults
      .filter(warning => selectedWarnings.includes(warning.aggregate.aggregateKey))
      .reduce((sum, current) => sum + current.reportRowCount, 0);
  };

  const renderTable = () => {
    const tableDataProvider = warningsResults.map(warning => mapWarningToTableData(warning.aggregate));

    const totalCount = tableDataProvider.length;

    const columnConfig: ColumnConfig = showTotalAfterTermed
      ? {
        breakdownBucket: { displayText: `# of ${warningType}`, testId: "excess-warnings-breakdown-bucket" },
        change: { alignment: TextAlignment.Right, displayText: "Change", testId: "excess-warnings-breakdown-change" },
        percentChange: { alignment: TextAlignment.Right, displayText: "% Change", testId: "excess-warnings-breakdown-percent-change" },
        providersCount: { alignment: TextAlignment.Right, displayText: "Current", testId: "excess-warnings-breakdown-providers-count" },
        totalAfterTermed: { alignment: TextAlignment.Right, displayText: "Total After Termed", testId: "excess-warnings-breakdown-total-after-termed" },
      }
      : {
        breakdownBucket: {
          displayText: `# of ${warningType}`,
          testId: "excess-warnings-breakdown-bucket",
        },
        providersCount: {
          alignment: TextAlignment.Right,
          displayText: `${networkIntegrityContext.providerType} Providers`,
          testId: "excess-warnings-breakdown-providers-count"
        },
      };

    const bulkActionBar = (
      <Link
        className="qes-button qes-button-secondary"
        id="excess-warnings-breakdown-create-report__link"
        to={{
          pathname: `/projects/${projectId}/${networkIntegrityContext.projectRunConfigurationId}/network-integrity/reports/${networkIntegrityContext.providerType}/${encodeURIComponent(aggregateGroup)}`,
          state: {
            initialProviderType: networkIntegrityContext.providerType,
            npiEntityType: npiEntityTypeFilter,
            reportRowCount: calculateReportsRowCount(),
            runConfiguration: networkIntegrityContext.projectRunConfiguration,
            selectedKeys: selectedWarnings,
            showConfidenceLevel: showConfidenceLevel
          }
        }}
        type="button" >
        Download Report
      </Link>
    );

    return (
      <div className="row">
        <div className="col-12">
          <Waffles<ExcessWarningsTableData>
            columns={columnConfig}
            bulkActionBar={tableDataProvider.length > 0 && bulkActionBar}
            data={tableDataProvider}
            testId="excess-warnings-breakdown-table"
            showCheckboxes={true}
            handleCheckboxChange={onCheckboxChange}
            selectedKeys={selectedWarnings}
            loading={loading}
            totalNumberOfRecords={totalCount}
            enablePagination={false}
            customSort={customSort}
          />
        </div>
      </div>
    );
  };

  return (
    <Page documentTitle="Network Integrity Excess Warnings Breakdown">
      <PageInner>
        <PageHeader title={`${warningType} Breakdown`} >
          <PageHeaderRight>
            <Link
              className="qes-button qes-button-secondary"
              type="button"
              id="network-integrity-excess-warnings-breakdown__back-to-project"
              to={{ pathname: `/projects/${projectId}/network-integrity`, state: { initialProviderType: networkIntegrityContext.providerType, initialRegionSetIds: networkIntegrityContext.regionSetIds, initialSpecialtyCodes: networkIntegrityContext.specialtyCodes, runConfiguration: networkIntegrityContext.projectRunConfiguration } }}
            >
              Return to Project
            </Link>
          </PageHeaderRight>
        </PageHeader>

        <PageSection>
          <NetworkIntegrityFilterContainer isViewAllPage isExcessWarningsBreakdownPage />
        </PageSection>

        {loading && (
          <PageSection>
            <LoadingSpinner />
          </PageSection>
        )}
        {!loading && (
          <PageSection>
            {renderTable()}
          </PageSection>
        )}

      </PageInner>
    </Page>
  );
};

export { ExcessWarningsBreakdownPage };